<template>
  <div class="" v-if="isPropertyLoading">
    <Loading />
  </div>

  <div v-else>
    <!--
      On /:sitecode/register always use BethsLayout: when the portal failed to load it
      renders its own "Something went wrong / Try again" or "Not Found" state instead of
      handing the slot below an empty client portal picker.
    -->
    <component
      :is="property || !isClientRegister ? BethsLayout : NonPropertyLayout"
      :layoutTitle="`Register - ${property ? property.name : clientName || 'SafetyNet'}`"
    >
      <div class="flex flex-col justify-center min-h-600">
        <div
          class="m-auto my-10 border-2 border-gray-300 shadow-md rounded-md w-12/12 xl:6/12 lg:w-7/12 md:w-9/12 sm:12/12"
        >
          <div class="px-8 py-8 rounded-xl">
            <h1
              id="login-title"
              class="mt-2 flex flex-wrap content-center text-center font-bold text-2xl"
            >
              <div class="font-normal">Register at&nbsp;</div>
              <div>
                {{ property?.name ?? clientName }}
              </div>
            </h1>
            <div class="mt-4 mb-5">
              <p id="loginLink" class="font-light text-base text-gray-400">
                Already have an account?
                <router-link to="login" class="font-bold underline text-lg text-blue-500">
                  Login
                </router-link>
              </p>
            </div>

            <!--  
              
              Client registration property selector
            
            -->
            <div v-if="!property && isClientRegister" class="text-sm">
              <BaseSelect
                id="portalSelect"
                label="Please select your home portal"
                :options="homePortalOptions"
                v-model="selectedHomePortal"
              />
              <BaseButton
                id="clientRegisterContinue"
                class="my-4 float-right"
                :disabled="selectedHomePortal == ''"
                @click="continueToBuildingRegister"
              >
                Continue
              </BaseButton>
            </div>
            <!-- 
              
              Invite code & email guard 
            
            -->
            <div
              v-else-if="!property?.usesSharedPasswordMode && !showFullRegistrationPage"
            >
              <ErrorDisplay
                :label="inviteCodeGuardError"
                :message="inviteCodeGuardErrorMessage"
                :isVisible="inviteCodeGuardError?.length > 0"
              />
              <!-- Use a form to handle "enter" keystroke -->
              <form v-on:submit.prevent class="h-max">
                <div
                  v-if="inviteCodes.length === 0 && !property.inviteCodeFieldsHidden"
                  class="flex-1 my-5 text-sm"
                  data-cy="required"
                >
                  <BaseInput
                    label="Invite Code"
                    id="invite"
                    autocomplete="off"
                    v-model="unappliedInviteCode"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    placeholder="An invite code sent in welcome email"
                    :error="guardErrors.unappliedInviteCode"
                  />
                </div>

                <div class="flex-1 my-5 text-sm" data-cy="required">
                  <BaseInput
                    id="username"
                    v-model="usernameGuard"
                    label="Username (email)*"
                    placeholder="work@email.com"
                    autocomplete="email"
                    type="email"
                    class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    :error="guardErrors.usernameGuard"
                  />
                </div>

                <div class="flex flex-row-reverse mt-8">
                  <BaseButton
                    class="w-40 flex py-3 px-3 text-xl text-center text-white duration-300 bg-btn hover:bg-blue-500 rounded"
                    aria-label="reload_invite"
                    @click="lookupEmailAndInviteCode"
                    type="submit"
                  >
                    <span v-if="loadingInviteCode">Loading...</span>
                    <span v-else id="continue_button">Continue</span>
                  </BaseButton>
                </div>
              </form>
            </div>

            <!-- 
              
              Main registration form 
            
            -->

            <div v-else>
              <ErrorDisplay
                label="Please correct errors below"
                :message="errorSavingMessage"
                :isVisible="errorSavingMessage != null"
              />
              <InfoDisplay :label="infoMessage" :isVisible="infoMessage != null" />

              <form class="mt-4">
                <div class="flex flex-row">
                  <div class="flex-1 mt-5 text-sm" data-cy="required">
                    <BaseInput
                      id="firstname"
                      v-model="firstName"
                      label="First Name*"
                      type="text"
                      autocomplete="given-name"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :error="errors.firstName"
                    />
                  </div>
                  <div class="flex-1 mt-5 ml-2 text-sm" data-cy="required">
                    <BaseInput
                      id="lastname"
                      v-model="lastName"
                      label="Last Name*"
                      autocomplete="family-name"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      type="text"
                      :error="errors.lastName"
                    />
                  </div>
                </div>

                <div class="flex flex-row">
                  <div
                    v-if="property?.usesSharedPasswordMode"
                    class="flex-1 my-5 text-sm mr-2"
                    data-cy="required"
                  >
                    <BaseInput
                      v-model="username"
                      label="Username (email)*"
                      placeholder="work@email.com"
                      autocomplete="email"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :error="errors.username"
                    />
                  </div>

                  <div class="flex-1 my-5 text-sm" data-cy="required">
                    <BaseInput
                      id="password"
                      v-model="newPassword"
                      label="Password*"
                      type="password"
                      autocomplete="new-password"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :error="errors.newPassword"
                    />
                  </div>
                </div>
                <div class="flex flex-row">
                  <div class="flex-1 text-sm">
                    <BaseInput
                      id="primaryPhone"
                      v-model="primaryPhone"
                      label="Primary Phone"
                      typeof="text"
                      autocomplete="tel"
                      type="tel"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :error="errors.primaryPhone"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div class="flex-1 ml-2 text-sm">
                    <BaseInput
                      id="mobilePhone"
                      v-model="mobilePhone"
                      label="Mobile Phone"
                      typeof="text"
                      autocomplete="tel"
                      type="tel"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :error="errors.mobilePhone"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>

                <div class="mt-4 flex" v-if="property?.isMobilityCheckEnabled">
                  <BaseCheckbox
                    v-model="mobilityImpaired"
                    label="I have a mobility impairment or special need and may require assistance during an emergency evacuation."
                    :error="errors.mobilityImpaired"
                    class="flex-1"
                  />
                  <div class="has-tooltip">
                    <span
                      class="tooltip rounded shadow-lg p-4 bg-gray-800 text-white -mt-80 -ml-24 xl:mr-24 xl:-mt-60 lg:-ml-16 lg:-mt-64 text-sm"
                    >
                      Checking this box will send an email to your building's Fire Safety
                      Director (or person responsible for the life safety program)
                      indicating that you may need help during an evacuation. Your name
                      may also appear on a list within this software of those who have
                      requested special help. However, checking this box does not
                      guarantee that assistance will be provided - please make sure to
                      follow up directly with your floor warden, building staff,
                      supervisor, or others responsible for the life safety program to
                      ensure proper procedures are in place to help you.
                    </span>
                    <IHeroiconsOutlineExclamationCircle />
                  </div>
                </div>
                <br />
                <div class="my-5 text-sm">
                  <BaseSelect
                    v-if="property?.floorList?.length > 0"
                    id="primaryFloor"
                    label="Primary Floor"
                    v-model="primaryFloor"
                    :options="floorList"
                    class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                  />
                  <BaseInput
                    v-else
                    class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                    label="Primary Floor"
                    v-model="primaryFloor"
                  />
                </div>

                <!-- <div class="my-5 text-sm" v-if="!usesSharedPasswordMode">
                  <div class="">
                    <label for="invite" class="block text-black">Invite Code</label>
                  </div>
                  <div v-if="inviteCodes.length > 0" class="mt-2 mb-1 ml-4 font-bold">
                    <div v-for="code in inviteCodes" :key="code.inviteCodeId">
                      {{ code.code }}
                    </div>
                  </div>
                  <div v-if="inviteCodeGuardError" class="w-max mt-3">
                    <span class="text-lg bg-yellow-400 p-2 px-4">
                      {{ inviteCodeGuardError }}
                    </span>
                  </div>

                  <div class="flex rounded-lg h-max">
                    <input
                      id="invite"
                      v-model="unappliedInviteCode"
                      type="text"
                      class="w-full px-4 text-xl font-mono py-3 mt-2 h-full bg-gray-200 border border-gray-400 focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      placeholder="An invite code sent in welcome email"
                    />
                    <BaseButton
                      class="p-2 mt-2 rounded-none text-lg"
                      aria-label="reload_invite"
                      type="button"
                      @click="lookupInviteCode"
                    >
                      <span v-if="loadingInviteCode">Loading...</span>
                      <span v-else>Apply</span>
                    </BaseButton>
                  </div>
                  <div class="flex-1">
                    <p v-if="inviteCodeError" class="text-red-800">
                      Enter Invite Code above and click "Apply"
                    </p>
                  </div>
                </div> -->

                <!-- <div
                  v-if="
                    inviteCodes.length > 0 &&
                    property?.isTrainingEnabled &&
                    !usesSharedPasswordMode
                  "
                  class="text-sm"
                >
                  <div>Training Programs Activated:</div>
                  <ul class="pl-4 pt-2">
                    <li
                      class="list-disc ml-4"
                      v-for="program in trainingPrograms"
                      :key="program.value"
                    >
                      {{ program.name }}
                    </li>
                  </ul>
                  <p v-if="trainingPrograms.length == 0">
                    <span class="text-gray-400 ml-4 font-mono">
                      (Apply invitecode above to view training programs)
                    </span>
                  </p>
                </div> -->

                <div v-if="property?.propertyType != PropertyType.SingleTenant" class="">
                  <div class="flex-1 my-5 text-sm">
                    <label for="tenant" class="block text-black mb-2">
                      {{ property?.tenantAlias ? property?.tenantAlias : "Tenant" }}*
                    </label>

                    <div class="relative">
                      <select
                        id="tenant"
                        v-model="tenant"
                        class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      >
                        <option
                          v-for="propTenant in tenants"
                          :key="propTenant.tenantId"
                          :value="propTenant.tenantId"
                        >
                          {{ propTenant.name }}
                        </option>
                      </select>
                      <p id="tenant-error" class="text-red-800">{{ errors.tenant }}</p>
                      <div
                        class="absolute flex items-center px-2 pointer-events-none right-3 top-5 text-grey-darker"
                      >
                        <svg
                          class="w-4 h-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="flex-1 my-5 text-sm">
                  <label for="roles" class="block text-black mb-2">Building Role*</label>

                  <div class="relative" data-cy="required">
                    <select
                      id="roles"
                      v-model="role"
                      class="w-full px-4 py-3 text-xl font-mono appearance-none bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                      :class="{ 'border-red-800': errors.role }"
                    >
                      <option
                        v-for="roleObj in roles"
                        :key="roleObj.siteRoleId"
                        :value="roleObj.siteRoleId"
                      >
                        {{ roleObj.roleAlias }}
                      </option>
                    </select>
                    <p id="building-role-error" class="text-red-800">{{ errors.role }}</p>
                    <div
                      class="absolute flex items-center px-2 pointer-events-none right-3 top-5 text-grey-darker"
                    >
                      <svg
                        class="w-4 h-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div v-if="property?.isAlertEnabled" class="mb-8 mt-8">
                  <hr class="mb-4" />
                  <p class="text-black text-lg mb-2">Emergency Contact Info</p>
                  <p class="text-sm mb-6">
                    If you would like to receive Safetynet notifications about emergencies
                    affecting this building, enter an email address and/or SMS-enabled
                    cell phone number.
                  </p>
                  <p></p>
                  <div class="flex flex-row">
                    <div class="text-sm flex-1">
                      <BaseInput
                        v-model="alertPhone"
                        label="Emergency Alert SMS"
                        placeholder="Cell# to text Alerts"
                        type="text"
                        class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                        autocomplete="tel"
                        :error="errors.alertPhone"
                      />
                    </div>
                    <div class="ml-2 text-sm flex-1">
                      <BaseInput
                        v-model="alertEmail"
                        label="Emergency Alert Email"
                        placeholder="Email to send alerts"
                        type="text"
                        class="w-full px-4 text-base font-mono py-3 mt-2 bg-gray-200 border border-gray-400 rounded-sm focus:border-black hover:bg-gray-100 focus:bg-gray-100"
                        autocomplete="email"
                        :error="errors.alertEmail"
                      />
                    </div>
                  </div>

                  <div>
                    <p class="mt-6 mb-3 text-sm">
                      Select floors that you want notifications for:
                    </p>
                    <div class="flex">
                      <BaseCheckbox
                        class="grow mt-1"
                        v-model="selectAllFloors"
                        label="All Floors"
                      />
                      <button
                        v-if="primaryFloor.length > 0"
                        class="p-1 mr-2 bg-white border border-gray-500 rounded-sm text-sm"
                        type="button"
                        @click="
                          () => {
                            if (
                              !selectAllFloors &&
                              !selectedFloors.includes(primaryFloor)
                            )
                              selectedFloors.push(primaryFloor);
                          }
                        "
                      >
                        Select my floor
                      </button>
                      <button
                        class="p-1 bg-white border border-gray-500 rounded-sm text-sm"
                        type="button"
                        @click="
                          () => {
                            if (!selectAllFloors) selectedFloors = [];
                          }
                        "
                      >
                        Clear Selection
                      </button>
                    </div>

                    <Listbox
                      v-model="selectedFloors"
                      multiple
                      :disabled="selectAllFloors"
                    >
                      <div class="relative mt-3">
                        <ListboxButton
                          class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                        >
                          <span
                            :class="[
                              selectAllFloors ? 'text-gray-500' : 'text-gray-800',
                              'text-md',
                            ]"
                          >
                            {{
                              selectAllFloors
                                ? "All floors selected"
                                : selectedFloors.length > 0
                                ? selectedFloors.join(", ")
                                : "-- select floors --"
                            }}
                          </span>
                          <span
                            class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                          >
                            <i-heroicons-solid-selector
                              :class="[
                                selectAllFloors ? 'text-gray-300' : 'text-gray-600',
                                'w-5 h-5 text-2xl',
                              ]"
                              aria-hidden="true"
                            />
                          </span>
                        </ListboxButton>
                        <transition
                          leave-active-class="transition duration-100 ease-in"
                          leave-from-class="opacity-100"
                          leave-to-class="opacity-0"
                        >
                          <ListboxOptions
                            class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                          >
                            <ListboxOption
                              v-for="floor in floorList"
                              :key="floor"
                              :value="floor"
                              as="template"
                            >
                              <li
                                :class="[
                                  selectedFloors.includes(floor)
                                    ? 'border-1 bg-sky-300 border-blue-500'
                                    : 'hover:bg-blue-200',
                                  'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                                ]"
                              >
                                {{ floor }}
                              </li>
                            </ListboxOption>
                          </ListboxOptions>
                        </transition>
                      </div>
                    </Listbox>
                  </div>
                  <div class="ml-4 mt-2">
                    <span class="text-xs mb-8 text-gray-600 italic">
                      For SMS alerts, message and data rates may apply. You can opt out at
                      any time by removing the number from your profile or replying STOP.
                      Contact your building management for support or send email to
                      support@akpreparedness.com.
                    </span>
                  </div>
                  <div class="mt-4">
                    <BaseCheckbox
                      v-model="pushOptOut"
                      label="I do not want to receive push notification alerts on any of my devices."
                    />
                  </div>
                </div>

                <div class="mt-8 text-sm">
                  <BaseCheckbox
                    v-model="acceptedTerms"
                    id="acceptedTerms"
                    label="I accept the <a href='terms' target='_blank' class='text-blue-500'>Terms of Use</a>*"
                    :error="errors.acceptedTerms"
                    data-cy="required"
                  />
                </div>
                <div
                  v-if="errorSavingMessage"
                  class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative"
                  role="alert"
                >
                  <span class="block sm:inline mt-4 py-2">
                    Check for errors above and try again.
                  </span>
                </div>
                <div v-if="isSaving" class="w-full text-center"><Loading /></div>

                <div class="flex flex-wrap justify-between flex-row-reverse mt-8">
                  <button
                    class="w-60 w- flex py-3 px-3 text-center text-white duration-300 bg-btn hover:bg-blue-500 rounded"
                    @click="createNewAccount"
                    data-cy="submit"
                    id="createAccountButton"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      width="28px"
                      height="28px"
                      stroke-width="1.5"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                      />
                    </svg>

                    <span class="flex-grow text-xl pr-5">Create Account</span>
                  </button>
                </div>
                <div class="text-sm">
                  <p>* Required field</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </component>
  </div>

  <BasePopup
    :isVisible="showEmailVerificationRequiredMessage"
    title="You are almost set up!"
    :is-full-width="false"
    @close="
      {
        showEmailVerificationRequiredMessage = false;
        continueRegistrationExecution();
      }
    "
  >
    <p>
      This portal requires email verification, please check your email for verification
      link to begin using SafetyNet.
    </p>
    <div class="float-right mt-4">
      <button
        class="text-lg font-medium px-4 py-2 mx-4 mt-5 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
        @click="
          {
            showEmailVerificationRequiredMessage = false;
            continueRegistrationExecution();
          }
        "
      >
        Continue
      </button>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";

import { useField, useForm, validate } from "vee-validate";
import * as yup from "yup";
import {
  InviteCodeDTO,
  RegisterRequest,
  SiteRoleDTO,
  PropertyType,
} from "../store/generatedFetchClient";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import { useRouter, useRoute } from "vue-router";
import { computed, ref, onMounted, watch } from "vue";

import BethsLayout from "../layouts/BethsLayout.vue";
import NonPropertyLayout from "../layouts/NonPropertyLayout.vue";
import {
  emailMatchesDomainListOrListEmpty,
  emailMatchesDomainList,
  validateUnrequiredPhoneNumber,
} from "../helpers";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

var returnUrl: string | null = null;
var domainWhitelist = [] as string[];

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
// Only /client/:clientsystemname/register should offer the home portal picker. On
// /:sitecode/register a null property means the portal failed to load, which BethsLayout
// reports; previously it fell through to an empty picker with a blank title.
var isClientRegister = computed(() => route.name === "client-register");
var usesSharedPasswordMode = computed(
  () => bldgStore.property?.usesSharedPasswordMode ?? false
);

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  await router.isReady();

  loadDataClientRegister();

  let returnUrls = route.query["returnUrl"];
  if (returnUrls) returnUrl = Array.isArray(returnUrls) ? returnUrls[0] : returnUrls;
  let user = route.query["user"];
  if (user) usernameGuard.value = Array.isArray(user) ? user[0] : user;

  if (user) {
    infoMessage.value =
      "An account for this email address has not been set up. Please register by filling out the fields below.";
    document.querySelector("#login-title")?.scrollIntoView({ behavior: "smooth" });
  }

  var inviteCode = router.currentRoute.value.query["invite"];
  console.log("route query", router.currentRoute.value);
  console.log("url invitecode", inviteCode);
  if (inviteCode) {
    var invite = Array.isArray(inviteCode) ? inviteCode[0] : inviteCode;
    unappliedInviteCode.value = invite;
    console.log(`Found passed in invite code(s): '${invite}'`);
    await lookupInviteCode();
  }

  if (domainWhitelist.length < 1) {
    domainWhitelist =
      property.value?.domainWhiteList?.map((d) => d?.trim().toLowerCase() ?? "") ?? [];
  }
}

var floorList = computed(() => {
  let floors = bldgStore.property?.floorList;
  return floors;
});

let isSaving = ref(false);

var tenants = computed(() => {
  if (property.value && property.value.tenants) {
    let tenants = property.value.tenants;
    let unrestrictedTenants = tenants.filter(
      (t) =>
        (!t.isRestricted && !t.isEmailVerificationRequired) ||
        activatedTenantIds.value.find((a) => a == t.tenantId) ||
        emailMatchesDomainList(t.emailDomainMatch, usernameGuard.value)
    );
    console.log("Active Tenants", unrestrictedTenants);
    return unrestrictedTenants;
  }
  return [];
});

var enableStaffRoles = ref(false);
var roles = computed(() => {
  if (bldgStore.property !== null) {
    let allRoles = bldgStore.property?.siteRoles ?? ([] as SiteRoleDTO[]);

    if (enableStaffRoles.value) {
      return allRoles;
    } else {
      return allRoles.filter((r) => !r.isStaffRole);
    }
  }
  return [];
});

// var trainingPrograms = computed(function () {
//   let bldg = bldgStore.building;
//   if (bldg !== null) {
//     let tp = bldg.trainingPrograms.filter((t) => {
//       for (let inviteCode of inviteCodes.value) {
//         if (inviteCode.trainingProgramIds.includes(t.trainingProgramId)) {
//           return true;
//         }
//       }
//       return false;
//     });

//     if (tp !== null && tp !== undefined) {
//       let options = tp.map((t) => {
//         return {
//           name: t.name,
//           value: t.trainingProgramId,
//           checked: true,
//         };
//       });
//       return options;
//     }
//   }
//   return [];
// });

//let showSocialButtons = ref(false);

//See: https://github.com/jquense/yup/blob/master/docs/typescript.md
// https://vee-validate.logaretm.com/v4/api/use-form

const validationSchema = yup.object({
  firstName: yup.string().required().label("First Name"),
  lastName: yup.string().required().label("Last Name"),
  username: yup
    .string()
    .email()
    .label("Username")
    .test(
      "is-required",
      (d) => "Username (email) is a required field",
      (value) => !usesSharedPasswordMode.value || value.length > 0
    )
    .test(
      "is-approved",
      (d) => `Not an approved email account`,
      (value) => {
        if (value == null) value = "";
        let emailDomain = value
          .substring(value.indexOf("@") + 1)
          .toLowerCase()
          .trim();

        return (
          domainWhitelist.length == 0 ||
          domainWhitelist.indexOf(emailDomain) >= 0 ||
          emailDomain.endsWith(".test")
        );
      }
    ),
  newPassword: yup.string().required().min(5).label("Password"),

  primaryPhone: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),
  mobilePhone: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),

  mobilityImpaired: yup.boolean().default(false),
  role: yup.number().required().label("Building Role"),

  tenant: yup
    .number()
    .nullable()
    .test(
      "valid-tenant-vr",
      (d) => `Tenant is a required field`,
      (value) => {
        //Do not require tenant if VR Tour is enabled
        //  or proprety is SingleTenant
        return (
          (property.value?.isVrtourEnabled ? value > 0 : true) ||
          property?.value?.propertyType == PropertyType.SingleTenant
        );
      }
    ),
  primaryFloor: yup.string().nullable().notRequired(),

  alertPhone: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      "valid-number",
      (d) => "Phone number is not valid",
      (value) => validateUnrequiredPhoneNumber(value)
    ),
  alertEmail: yup.string().nullable().notRequired(),

  acceptedTerms: yup.bool().default(false).oneOf([true], "Please accept Terms"),
});

const { handleSubmit, errors } = useForm({
  validationSchema,
  initialValues: {
    firstName: "",
    lastName: "",
    username: "",
    newPassword: "",

    primaryPhone: undefined,
    mobilePhone: undefined,

    mobilityImpaired: false,
    role: undefined,

    tenant: undefined,
    primaryFloor: "",

    alertPhone: undefined,
    alertEmail: undefined,

    acceptedTerms: false,
  },
});

const { value: firstName } = useField<string>("firstName");
const { value: lastName } = useField<string>("lastName");
const { value: username } = useField<string>("username");
const { value: newPassword } = useField<string>("newPassword");

const { value: primaryPhone } = useField<string>("primaryPhone");
const { value: mobilePhone } = useField<string>("mobilePhone");

const { value: mobilityImpaired } = useField<boolean>("mobilityImpaired");

const trainingProgram = ref<Number[]>([]);
const inviteCodeValidate = async () => {
  if (!property.value?.isTrainingEnabled) {
    inviteCodeError.value =
      inviteCodes?.value?.length === 0 && !property.value?.inviteCodeFieldsHidden
        ? "True"
        : "";
    return;
  }

  inviteCodeError.value =
    (
      await validate(
        trainingProgram.value,
        yup.array<Number>().required().min(1).label("Training Program") as any
      )
    ).errors[0] ?? "";
};
const inviteCodeError = ref("");

const { value: role } = useField<number>("role");

const { value: tenant } = useField<number>("tenant");
const { value: primaryFloor } = useField<string>("primaryFloor");

const { value: alertPhone } = useField<string>("alertPhone");
const { value: alertEmail } = useField<string>("alertEmail");

const { value: acceptedTerms } = useField<boolean>("acceptedTerms");
const pushOptOut = ref(false);

const selectAllFloors = ref(false);
const selectedFloors = ref([] as string[]);

watch(selectedFloors, () => {
  selectedFloors.value.sort(
    (a, b) => floorList.value.indexOf(a) - floorList.value.indexOf(b)
  );
});

/**
 * InviteCode code
 */

let loadingInviteCode = ref(false);
let inviteCodeGuardError = ref("");
let inviteCodeGuardErrorMessage = ref("");
let inviteCodes = ref<InviteCodeDTO[]>([]);
let activatedTenantIds = ref<number[]>([]);

let showFullRegistrationPage = ref(false);

const { handleSubmit: guardHandleSubmit, errors: guardErrors } = useForm({
  validationSchema: yup.object({
    unappliedInviteCode: yup
      .string()
      .test(
        "is-required",
        (d) => "Invite Code is a required field",
        (value) => {
          // console.log(
          //   "Invite code required if blank: ",
          //   property.value?.tenants?.filter(
          //     (t) =>
          //       t.emailDomainMatch != null &&
          //       t.emailDomainMatch.length > 0 &&
          //       emailMatchesDomainListOrListEmpty(t.emailDomainMatch, usernameGuard.value)
          //   )
          // );
          return (
            (value && (value.length > 0 || inviteCodes.value.length > 0)) ||
            (property.value?.inviteCodeFieldsHidden ?? false) ||
            property.value?.tenants?.filter(
              (t) =>
                t.emailDomainMatch != null &&
                t.emailDomainMatch.length > 0 &&
                emailMatchesDomainListOrListEmpty(t.emailDomainMatch, usernameGuard.value)
            ).length > 0
          );
        }
      )
      .label("Invite Code"),

    // If Property.DomainWhiteList exists, all email accounts
    // must be from one of the domains.
    usernameGuard: yup
      .string()
      .required()
      .email()
      .label("Username")
      .test(
        "is-james",
        (d) => `Not an approved email account`,
        (value) => {
          if (value == null) value = "";
          let emailDomain = value
            .substring(value.indexOf("@") + 1)
            .toLowerCase()
            .trim();

          return (
            domainWhitelist.length == 0 ||
            domainWhitelist.indexOf(emailDomain) >= 0 ||
            emailDomain.endsWith(".test")
          );
        }
      ),
  }),
  initialValues: {
    unappliedInviteCode: "",
    usernameGuard: "",
  },
});

const { value: unappliedInviteCode } = useField<string>("unappliedInviteCode");
const { value: usernameGuard } = useField<string>("usernameGuard");

let lookupEmailAndInviteCode = guardHandleSubmit(
  async () => {
    if (usernameGuard.value?.length > 0 && !guardErrors.value.usernameGuard) {
      //Lookup email. See if it exists and if it needs v4 upgrade
      var upgradeMsg = await userStore.needsUpgrade(
        usernameGuard.value,
        property.value.portalId
      );
      console.log("upgrademsg", upgradeMsg);

      if (upgradeMsg?.includes("Account Disabled")) {
        inviteCodeGuardError.value = "This account has been disabled.";
        inviteCodeGuardErrorMessage.value = "Please contact your building administrator.";
        return;
      }

      if (!upgradeMsg || upgradeMsg.length == 0 || upgradeMsg.includes("Not Found")) {
        // If account doesn't exist, show registration (this) page

        // First apply potentially entered code
        await lookupInviteCode();

        let matchedTenants = property.value.tenants.filter((t) =>
          emailMatchesDomainList(t.emailDomainMatch, usernameGuard.value)
        );

        if (matchedTenants.length > 0) {
          tenant.value = matchedTenants[0].tenantId;

          // Get invite codes to auto activate
          let defaultCodes = await bldgStore.getAutoActivateInviteCodes(
            property.value.portalId
          );
          console.log("default codes", defaultCodes);
          //apply each default code individually
          if (typeof defaultCodes !== "string") {
            for (let i = 0; i < defaultCodes.length; i++) {
              unappliedInviteCode.value = defaultCodes[i].code;
              await lookupInviteCode();
            }
          }
        }

        if (inviteCodes.value?.length > 0 || matchedTenants.length > 0) {
          username.value = usernameGuard.value;
          showFullRegistrationPage.value = true;
        } else {
          console.log("invalid email");
          inviteCodeGuardError.value = "Unable to determine your tenant.";
          inviteCodeGuardErrorMessage.value =
            "Please enter your work email or click on a link you received in a welcome email.";
        }
      } else if (upgradeMsg?.includes("upgrade_needed")) {
        await lookupInviteCode();
        // If Account exists and needs upgrade, and invite code is valid, send to SetPersonalPassword.vue
        if (inviteCodes.value?.length > 0)
          router.push(
            `/${property.value.siteCode}/setpassword?user=${encodeURIComponent(
              usernameGuard.value
            )}&oldpass=${inviteCodes.value[0].code}`
          );
      } else if (upgradeMsg?.includes("not_needed")) {
        // If Account exists and doesn't need upgrade, send to login page with email filled out
        router.push(
          `/${property.value.siteCode}/login?user=${encodeURIComponent(
            usernameGuard.value
          )}&existing=true`
        );
      }
    }
  },
  (ctx: any) => {
    inviteCodeGuardError.value = Object.values(ctx.errors).join(", ");
    inviteCodeGuardErrorMessage.value = " ";
  }
);

async function lookupInviteCode() {
  if (property && property.value) {
    loadingInviteCode.value = true;
    inviteCodeGuardError.value = "";

    if (!unappliedInviteCode.value) {
      inviteCodeGuardError.value = "";
      loadingInviteCode.value = false;
      return;
    }

    let portalId = property.value.portalId ?? 0;
    try {
      let result = await bldgStore.lookupInviteCode(portalId, unappliedInviteCode.value);
      console.log("lookupInviteCode", result);
      if (typeof result === "object") {
        let newCode = result as InviteCodeDTO;
        console.log("Found lookup code", newCode);
        if (!inviteCodes.value.find((i) => i.inviteCodeId === newCode.inviteCodeId)) {
          inviteCodes.value.push(newCode);
          activateCodes(inviteCodes.value);
          bldgStore.building.trainingPrograms.forEach((t) =>
            inviteCodes.value.forEach((i) => {
              if (i.trainingProgramIds.includes(t.trainingProgramId))
                trainingProgram.value.push(t.trainingProgramId);
            })
          );
        }

        if (showFullRegistrationPage.value) unappliedInviteCode.value = "";
        inviteCodeGuardError.value = "";
      } else {
        inviteCodeGuardError.value = (result as string) ?? "";
        inviteCodeGuardErrorMessage.value =
          "Please try entering it again or ask your admin contact for a new one";
      }
      loadingInviteCode.value = false;
    } catch (apiError: any) {
      inviteCodeGuardError.value = "error";
      inviteCodeGuardErrorMessage.value =
        "Please try entering it again or ask your admin contact for a new one";
      loadingInviteCode.value = false;
    }
  }

  await inviteCodeValidate();
}

// let staffRolesEnabled = ref(false);
// let trainProgIdsEnabled = ref<number[]>([]);

//Set form state based on all active invite codes
function activateCodes(codes: InviteCodeDTO[]) {
  console.log("Activating invite code", codes);
  enableStaffRoles.value = false;

  codes.forEach((code) => {
    if (code.enableStaffRoles) {
      enableStaffRoles.value = true;
    }
    if (code.activatedTenantId) {
      activatedTenantIds.value.push(code.activatedTenantId);
      tenant.value = code.activatedTenantId;
    }
  });
}

let submitSuccess = ref(false);

let showEmailVerificationRequiredMessage = ref(false);
const resume = ref<(() => void) | null>(null);
function waitForUserToSeeVerificationMessage(): Promise<void> {
  return new Promise((resolve) => {
    resume.value = resolve;
  });
}
function continueRegistrationExecution() {
  if (resume.value) {
    resume.value(); // Resolve the promise
    resume.value = null; // Clear the reference
  }
}

let onInvalidSubmit = function (ctx: any) {
  if (ctx?.errors) {
    isSaving.value = false;
    submitSuccess.value = false;
    errorSavingMessage.value = Object.values(ctx.errors).join(", ");
    infoMessage.value = null;
    console.log("validation error trying to submit form", ctx);
  }
};

let errorSavingMessage = ref<string | null>(null);
let infoMessage = ref<string | null>(null);

let createNewAccount = handleSubmit(async (values) => {
  isSaving.value = false;
  errorSavingMessage.value = null;
  infoMessage.value = null;
  submitSuccess.value = false;

  if (!usesSharedPasswordMode.value) {
    if (unappliedInviteCode.value) await lookupInviteCode();
    else await inviteCodeValidate();

    console.log(inviteCodeError.value);
    console.log(unappliedInviteCode.value);
    if (inviteCodeError.value || unappliedInviteCode.value) {
      errorSavingMessage.value = "Please enter a valid Invite Code to Register";
      return;
    }
  }

  console.log("Creating new Account...", values);

  if (property.value?.propertyType === PropertyType.SingleTenant) {
    // If Single Tenant, set tenant to 0
    tenant.value = tenants.value.length > 0 ? tenants.value[0].tenantId : 0;
    values.tenant = tenant.value;
  } else if (tenant.value == null || tenant.value < 1) {
    errorSavingMessage.value = "Tenant is a required field";
    return;
  }

  var registrationData = {
    inviteCodeIds: inviteCodes.value.map((i) => i.inviteCodeId),
    loginEmail: property.value.usesSharedPasswordMode
      ? username.value
      : usernameGuard.value,
    password: values.newPassword,
    firstName: values.firstName,
    lastName: values.lastName,
    siteRoleId: values.role,
    tenantId: values.tenant,
    primaryFloor: values.primaryFloor,
    mobilePhone: values.mobilePhone,
    primaryPhone: values.primaryPhone,
    isMobilityImpaired: values.mobilityImpaired,
    portalId: property.value.portalId,
    trainingProgramIds: trainingProgram.value,
    contactSms: values.alertPhone,
    contactEmail: values.alertEmail,
    alertSubscribedFloors: selectAllFloors.value ? null : selectedFloors.value.toString(),
    isPushOptedOut: pushOptOut.value,
  } as RegisterRequest;

  isSaving.value = true;
  try {
    let errorString = await userStore.createAccount(registrationData);
    errorSavingMessage.value = errorString;
    if (errorString == null) {
      var user = await userStore.getUser(
        bldgStore.siteCode,
        bldgStore.property.portalId,
        true
      );

      // The account call can succeed while the follow-up lookup fails
      if (!user) {
        userStore.setAuthToken("");
        errorSavingMessage.value =
          "We couldn't sign you in to this account. Please try logging in, or contact your building administrator.";
        document.querySelector("#login-title")?.scrollIntoView({ behavior: "smooth" });
        return;
      }

      if (
        property.value.tenants.find((t) => t.tenantId == user.tenantId)
          ?.isEmailVerificationRequired
      ) {
        userStore.requestEmailVerification(user.loginEmail, property.value.siteCode);

        showEmailVerificationRequiredMessage.value = true;
        await waitForUserToSeeVerificationMessage();
      }

      if (user.token) {
        // One call covers every API client - the token is shared in ClientBase.
        userStore.setAuthToken(user.token);
      }

      await bldgStore.tryGetBuildingData(bldgStore.siteCode, true);
      if (returnUrl && returnUrl.startsWith("/" + property.value.siteCode)) {
        router.replace(returnUrl);
      } else {
        router.replace("/" + property.value.siteCode);
      }
    } else {
      document.querySelector("#login-title")?.scrollIntoView({ behavior: "smooth" });
    }
  } finally {
    // Always stop the spinner, or an unexpected error leaves it running forever
    isSaving.value = false;
  }
}, onInvalidSubmit);

// #region Client Register

var homePortalOptions = computed(() =>
  property.value
    ? []
    : bldgStore.portalList.map((p) => ({ name: p.name, value: p.portalId }))
);
var selectedHomePortal = ref<string>(""); // BaseInput updates v-model as string instead of number for some reason

var clientName = ref("");

async function loadDataClientRegister() {
  var routeClientPosition = router.currentRoute.value.path.indexOf("/client/");
  if (routeClientPosition >= 0) {
    var clientSystemName = router.currentRoute.value.path.substring(
      routeClientPosition + 8,
      route.path.indexOf("/register")
    );
    await bldgStore.getPortalList(clientSystemName);
    clientName.value = bldgStore.portalList[0]?.clientName;
  }
}

function continueToBuildingRegister() {
  var portalId = Number.parseInt(selectedHomePortal.value);
  var siteCode = bldgStore.portalList.find((p) => p.portalId === portalId).siteCode;
  router.push(`/${siteCode}/register`);
}

// #endregion Client Register
</script>

<style>
.tooltip {
  @apply invisible absolute;
}

.has-tooltip:hover .tooltip {
  @apply visible z-50;
}
</style>
